/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useEffect, useRef, useState } from 'react';

const POSITIONED_CONTAINER_STYLE = css`
  label: POSITIONED_CONTAINER_STYLE;
  z-index: 99999;
`;

/** Minimum distance from viewport/parent edge to prevent container from touching the edge */
const EDGE_PADDING = 8;

const toLocalCoords = (viewportX: number, viewportY: number, parentRect: DOMRect | null) =>
  parentRect
    ? { x: viewportX - parentRect.left, y: viewportY - parentRect.top }
    : { x: viewportX, y: viewportY };

const getDefaultPosition = (
  targetRect: DOMRect,
  containerRect: DOMRect,
  parentRect: DOMRect | null,
  placement: 'top' | 'bottom' | 'left' | 'right',
  offsetX: number,
  offsetY: number,
) => {
  const target = toLocalCoords(targetRect.left, targetRect.top, parentRect);
  const targetRight = target.x + targetRect.width;
  const targetBottom = target.y + targetRect.height;

  let left = 0;
  let top = 0;

  switch (placement) {
    case 'bottom':
      left = target.x;
      top = targetBottom;
      break;
    case 'top':
      left = target.x;
      top = target.y - containerRect.height;
      break;
    case 'right':
      left = targetRight;
      top = target.y;
      break;
    case 'left':
      left = target.x - containerRect.width;
      top = target.y;
      break;
  }

  return { left: left + offsetX, top: top + offsetY };
};

/** Clamp container position to stay within bounds with minimum movement */
const ensureOnScreen = (
  left: number,
  top: number,
  containerWidth: number,
  containerHeight: number,
  parentRect: DOMRect | null,
) => {
  const boundsWidth = parentRect?.width ?? window.innerWidth;
  const boundsHeight = parentRect?.height ?? window.innerHeight;

  const maxLeft = boundsWidth - containerWidth - EDGE_PADDING;
  const maxTop = boundsHeight - containerHeight - EDGE_PADDING;

  return {
    left: Math.max(EDGE_PADDING, Math.min(left, maxLeft)),
    top: Math.max(EDGE_PADDING, Math.min(top, maxTop)),
  };
};

const findPositionedParent = (element: HTMLElement | null): HTMLElement | null => {
  if (!element || element === document.body) return null;
  const style = window.getComputedStyle(element);
  if (style.position === 'fixed' || style.position === 'absolute' || style.transform !== 'none') {
    return element;
  }
  return findPositionedParent(element.parentElement);
};

export const PositionedContainer = ({
  targetRef,
  placement = 'bottom',
  offsetX = 0,
  offsetY = 0,
  children,
  ...containerProps
}: {
  targetRef: React.RefObject<HTMLElement | null>;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  offsetX?: number;
  offsetY?: number;
  children: React.ReactNode;
} & Omit<React.HTMLAttributes<HTMLDivElement>, 'children'>) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number; position: 'fixed' | 'absolute' } | null>(null);

  useEffect(() => {
    if (!targetRef.current) {
      setPosition(null);
      return;
    }

    const getPositioningContext = () => {
      const positionedParent = findPositionedParent(targetRef.current?.parentElement ?? null);
      const useAbsolute = positionedParent && positionedParent !== document.body;
      const parentRect = useAbsolute ? positionedParent!.getBoundingClientRect() : null;
      return { useAbsolute, parentRect };
    };

    const setInitialPosition = () => {
      if (!targetRef.current) return;

      const targetRect = targetRef.current.getBoundingClientRect();
      const { useAbsolute, parentRect } = getPositioningContext();
      const { x: left, y: top } = toLocalCoords(targetRect.left, targetRect.bottom, parentRect);

      setPosition({
        top: top + offsetY,
        left: left + offsetX,
        position: useAbsolute ? 'absolute' : 'fixed',
      });
    };

    setInitialPosition();

    const calculatePosition = () => {
      if (!targetRef.current || !containerRef.current) return;

      const targetRect = targetRef.current.getBoundingClientRect();
      const containerRect = containerRef.current.getBoundingClientRect();
      const { useAbsolute, parentRect } = getPositioningContext();

      const { left: defaultLeft, top: defaultTop } = getDefaultPosition(targetRect, containerRect, parentRect, placement, offsetX, offsetY);
      const { left, top } = ensureOnScreen(defaultLeft, defaultTop, containerRect.width, containerRect.height, parentRect);

      setPosition({ top, left, position: useAbsolute ? 'absolute' : 'fixed' });
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(calculatePosition);
    });
  }, [targetRef, placement, offsetX, offsetY]);

  if (!position) return null;

  const { top, left, position: positionType } = position;

  return (
    <div
      ref={containerRef}
      css={POSITIONED_CONTAINER_STYLE}
      style={{
        position: positionType,
        top: `${top}px`,
        left: `${left}px`,
      }}
      {...containerProps}
    >
      {children}
    </div>
  );
};
